import { Component, OnInit, ViewChild, ElementRef, HostListener, output, Input } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators, ValueChangeEvent } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { HttpClient } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { TranslateModule } from '@ngx-translate/core';
import { ExternalLoginService, IExternalLoginData, LanguageService, LoaderService, StorageService } from 'core-lib';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { SettingsService } from 'core-lib';
import { ICartItem, IShopCart, IProfileData } from 'core-lib';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ProfileValidationIconComponent } from "../profile-validation-icon/profile-validation-icon.component";
import intlTelInput from 'intl-tel-input';
import { MessageModule } from 'primeng/message';

/** Window event used by the demo settings selector to set the country (p-select value can't be set through the DOM). Detail: alpha2 code. */
export const DEMO_FILL_COUNTRY_EVENT = 'hlx-demo-fill-country';

/** custom intl-tel-input-validator */
export function intlTelInputValidator(itiInstance:any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (itiInstance) {
      let valid = itiInstance.isValidNumber();
      return valid ? null : {invalidPhoneNumber: {value: control.value}};
    }
    return {invalidPhoneNumber: {value: control.value}}
  };
}

@Component({
    selector: 'app-profile-booker-details',
    imports: [ReactiveFormsModule, InputTextModule, SelectModule, ButtonModule, TextareaModule, TranslateModule, CheckboxModule, DatePickerModule, IconFieldModule, InputIconModule, ProfileValidationIconComponent, MessageModule],
    templateUrl: './profile-booker-details.component.html',
    styleUrl: './profile-booker-details.component.scss'
})

export class ProfileBookerDetailsComponent implements OnInit {

  @Input() cartItem:ICartItem;
  profileData = output<IProfileData>();

  @ViewChild('iti') iti:ElementRef;
  itiInstance:any = null;

  externalLogin:IExternalLoginData|null = null;

  countries:any = null;
  etaValues:any = [
    { key: "00:00 - 01:00", value: "00:00 - 01:00" },
    { key: "01:00 - 02:00", value: "01:00 - 02:00" },
    { key: "02:00 - 03:00", value: "02:00 - 03:00" },
    { key: "03:00 - 04:00", value: "03:00 - 04:00" },
    { key: "04:00 - 05:00", value: "04:00 - 05:00" },
    { key: "05:00 - 06:00", value: "05:00 - 06:00" },
    { key: "06:00 - 07:00", value: "06:00 - 07:00" },
    { key: "07:00 - 08:00", value: "07:00 - 08:00" },
    { key: "08:00 - 09:00", value: "08:00 - 09:00" },
    { key: "09:00 - 10:00", value: "09:00 - 10:00" },
    { key: "10:00 - 11:00", value: "10:00 - 11:00" },
    { key: "11:00 - 12:00", value: "11:00 - 12:00" },
    { key: "12:00 - 13:00", value: "12:00 - 13:00" },
    { key: "13:00 - 14:00", value: "13:00 - 14:00" },
    { key: "14:00 - 15:00", value: "14:00 - 15:00" },
    { key: "15:00 - 16:00", value: "15:00 - 16:00" },
    { key: "16:00 - 17:00", value: "16:00 - 17:00" },
    { key: "17:00 - 18:00", value: "17:00 - 18:00" },
    { key: "18:00 - 19:00", value: "18:00 - 19:00" },
    { key: "19:00 - 20:00", value: "19:00 - 20:00" },
    { key: "20:00 - 21:00", value: "20:00 - 21:00" },
    { key: "21:00 - 22:00", value: "21:00 - 22:00" },
    { key: "22:00 - 23:00", value: "22:00 - 23:00" },
    { key: "23:00 - 00:00", value: "23:00 - 00:00" }
  ];
  language:string = ''

  profile: FormGroup = new FormGroup({
    firstName: new FormControl(null, [Validators.required]),
    lastName: new FormControl<string|null>(null, [Validators.required]),
    emailAddress: new FormControl<string|null>(null, [Validators.required, Validators.email]),
    phoneNumber: new FormControl<string|null>(null, [Validators.required]), //custom phone validation is added after itiInstance is set.
    address: new FormControl<string|null>(null, [Validators.required]),
    zipCode: new FormControl<string|null>(null, [Validators.required]),
    city: new FormControl<string|null>(null, [Validators.required]),
    country: new FormControl<string|null>(null, [Validators.required]),
    companyName: new FormControl<string|null>(null), // optional, shown only if profile.askForCompanyName is enabled
    eta: new FormControl<any|null>("15:00 - 16:00"),
    message: new FormControl<string|null>(null),  
    _phone_full: new FormControl<string|null>(null),
    marketingPermission: new FormControl<boolean>(false),
    // external login related fields, not shown in UI
    externalLink: new FormControl<{type: string, id: string}|null>(null),
    id: new FormControl<number|null>(null)
  });

  constructor(
    private http: HttpClient, 
    private languageService: LanguageService, 
    private externalLoginService: ExternalLoginService,
    private loader: LoaderService,
    public settings: SettingsService,
    public storage: StorageService,    
  ) {}

  ngOnInit() {

    this.language = this.languageService.getCurrentLanguage();

    this.loadCountryList();
    this.fillProfileFromCartItem();

    this.profile.controls['phoneNumber'].valueChanges.subscribe((val:any) => {
      let countryCode = this.itiInstance.getSelectedCountryData().dialCode;
      let fullValue = `+${countryCode}${val}`;
      this.profile.controls['_phone_full'].setValue(fullValue)
    });

    this.applyEtaRestrictions();

    let extLogin = this.storage.loadSessionStorage('extLogin');
    if (extLogin) {
      let sessionObj:IExternalLoginData = JSON.parse(atob(extLogin));
      if (sessionObj.email && sessionObj.membershipNumber && sessionObj.loginProvider == this.settings.get('externalLoginProvider')) {
        this.externalLogin = sessionObj;
        this.getExternalProfileData();
      }
    }


    
    //check the validation status on load also so that other components always have some data to show. SetTimeout prevents an error.
    setTimeout(() => {this.checkValidationStatus()}, 100);
    
    this.profile.valueChanges.subscribe((val:any) => {
      this.checkValidationStatus();
    });

    
  }

  getExternalProfileData() {
    let loginProvider = this.settings.get('externalLoginProvider');
    let externalId = this.externalLogin?.membershipNumber ?? '';
    let emailAddress = this.externalLogin?.email ?? '';
    this.loader.setLoader('loader.fetchingExternalProfile');
    this.externalLoginService.getExternalGuestProfile(loginProvider, externalId, emailAddress).subscribe({
      next: (res) => {
      if (res.guestProfile) {
        this.fillGuestProfileAndDisableFields(res.guestProfile);
      }
      },
      error: (error) => {
        console.error('Error fetching external profile data:', error);
      },
      complete: () => {
        this.loader.hideLoader();
      }
    });
  }

  fillGuestProfileAndDisableFields(guestProfile:any) {
    this.profile.controls['firstName'].setValue(guestProfile.firstName ?? '');
    this.profile.controls['lastName'].setValue(guestProfile.lastName ?? '');
    this.profile.controls['emailAddress'].setValue(guestProfile.emails?.[0] ?? '');
    this.profile.controls['phoneNumber'].setValue(guestProfile.mobilePhoneNumber ?? '');
    this.profile.controls['address'].setValue(guestProfile.addressLine1 ?? '');
    this.profile.controls['zipCode'].setValue(guestProfile.postalCode ?? '');
    this.profile.controls['city'].setValue(guestProfile.city ?? '');
    this.profile.controls['country'].setValue(guestProfile.countryISO?.toLowerCase());
    this.profile.controls['externalLink'].setValue(guestProfile.externalLink ?? null);
    this.profile.controls['id'].setValue(guestProfile.id ?? null);

    this.profile.controls['firstName'].disable();
    this.profile.controls['lastName'].disable();
    this.profile.controls['emailAddress'].disable();
    this.profile.controls['phoneNumber'].disable();
    this.profile.controls['address'].disable();
    this.profile.controls['zipCode'].disable();
    this.profile.controls['city'].disable();
    this.profile.controls['country'].disable();

    this.checkValidationStatus();
  }

  logout() {
    this.externalLoginService.logout();
  }

  checkValidationStatus() {
    const invalidControls: string[] = [];
    const disabledKeys: string[] = [];

    Object.keys(this.profile.controls).forEach(key => {
      const ctrl = this.profile.controls[key];
      if (ctrl.disabled) {
        disabledKeys.push(key);
        ctrl.enable({ emitEvent: false });
        if (ctrl.invalid) {
          invalidControls.push(key);
        }
        ctrl.disable({ emitEvent: false });
      }
      else if (ctrl.invalid) {
        invalidControls.push(key);
      }
    });

    const isValid = invalidControls.length === 0;

    this.profileData.emit({
      isValid: isValid,
      invalidControls: invalidControls,
      profile: this.profile.getRawValue()
    });

    disabledKeys.forEach(key => this.profile.controls[key].disable({ emitEvent: false }));
  }


  ngAfterViewInit() {

    this.itiInstance = intlTelInput(this.iti.nativeElement, {
      initialCountry: 'fi',
      separateDialCode: true,
      loadUtilsOnInit: "https://cdn.jsdelivr.net/npm/intl-tel-input@24.6.0/build/js/utils.js",
    });

    this.iti.nativeElement.addEventListener("countrychange", () => {
      let val = this.profile.controls['phoneNumber'].value;
      let countryCode = this.itiInstance.getSelectedCountryData().dialCode;
      let fullValue = `+${countryCode}${val}`;
      this.profile.controls['_phone_full'].setValue(fullValue);
    });
    //add validator to phone, which requires itiInstance to be initilialized
    this.profile.get('phoneNumber')?.addValidators(intlTelInputValidator(this.itiInstance));

  }

  loadCountryList() {
    /* countrylists are from https://stefangabos.github.io/world_countries/*/

    this.http.get(`./countries/${this.language}/countries.json`).subscribe((res:any) => {
      let prioInSettings = this.settings.get('profile.countryListPriority');
      let sorted;
      if (prioInSettings && Array.isArray(prioInSettings) && prioInSettings.length > 0) {
        sorted = this.moveCountriesToFirst(prioInSettings, res);
      }
      else {
        sorted = this.moveCountriesToFirst(['fi','se'], res);
      }
      
      this.countries = sorted;
    });
  }

  moveCountriesToFirst(alpha2array: string[], allCountries:any[]) {
    alpha2array.reverse();
    alpha2array.forEach((country:string, index:any) => {
      let ind = allCountries.findIndex((item:any) => {return item.alpha2 == country});
      if (ind) {
        let item = allCountries.splice(ind, 1);
        allCountries.unshift(...item);
      }
    });
    return allCountries;
    
  }

  @HostListener(`window:${DEMO_FILL_COUNTRY_EVENT}`, ['$event'])
  onDemoFillCountry(event: CustomEvent<string>) {
    this.profile.controls['country'].setValue(event.detail);
  }

  fillProfileFromCartItem() {
    let prof = this.cartItem.profile;
    if (prof) {
      Object.keys(prof).forEach((fieldName:string) => {
        this.profile.get(fieldName)?.setValue(prof[fieldName]);
      });
    }
  }

  applyEtaRestrictions() {
    const minHourRaw = this.settings.get("profile.etaMinHour");
    const maxHourRaw = this.settings.get("profile.etaMaxHour");

    const minHour = parseInt(minHourRaw);
    const maxHour = parseInt(maxHourRaw);

    const hasMin = !isNaN(minHour);
    const hasMax = !isNaN(maxHour);

    if (!hasMin && !hasMax) {
      return;
    }

    this.etaValues = this.etaValues.filter((item:any) => {
      const match = item.value.match(/^(\d{2}):/);
      if (!match) return false;

      const startHour = parseInt(match[1]);

      if (hasMin && hasMax) {
        return startHour >= minHour && startHour <= maxHour;
      } else if (hasMin) {
        return startHour >= minHour;
      } else if (hasMax) {
        return startHour <= maxHour;
      }

      return true; // fallback, shouldn't happen
    });
  }
}
